
import { TeamLibraryConst } from '@/constants/teamlibrary'
import helpers from '@/modules/library/helpers'
import { FileWithMetadata } from '@/modules/library/types'
import { Namespace } from '@/store/types'
import { ValidationRules } from '@/utils/input-validation'
import IconBin from '@svg/IconBin.svg'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import IconUploadCloud from '@svg/IconUploadCloud.svg'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import { Getter } from 'vuex-class'

/**
 * DragDrop Component
 *
 * A Vue component that provides 3d model upload functionality through drag-and-drop or file selection.
 * Supports validation of file types and sizes, displays validation status, and manages file metadata.
 *
 * Features:
 * - Drag-and-drop file upload with visual feedback
 * - Click-to-select file input with optional multiple file selection
 * - File validation (type, size) with configurable constraints
 * - Visual indication of valid/invalid files with status messages
 * - File removal capability
 * - Responsive design with mobile optimizations
 *
 * Props:
 * - multiple: boolean - Enable/disable multiple file selection (default: true)
 * - accept: string[] - Array of accepted file types/extensions (e.g., ['.glb', '.usdz'])
 * - isUploading: boolean - Flag indicating upload in progress (default: false)
 *
 * Events:
 * - files-selected: Emitted when files are selected, removed, or cleared with the current file list
 *
 * Methods:
 * - clearFiles(): Clears all selected files and resets the file input
 */
@Component({
  components: {
    IconBin,
    IconCheckCircleFilled,
    IconCloseFilled,
    IconUploadCloud,
  },
})
export default class DragDrop extends Vue {
  @Prop({ type: Boolean, default: true }) readonly multiple!: boolean
  @Prop({ type: Boolean, default: true }) readonly fileSizeCheck!: boolean
  @Prop({ type: Boolean, default: false }) readonly fileDimensionCheck!: boolean
  @Prop({ type: Array, default: [] }) readonly accept!: string[]
  @Prop({ type: Boolean, default: false }) readonly isUploading!: boolean
  @Prop({ type: String, default: '' }) readonly customInstruction!: string
  @Prop({ type: Number, default: 0 }) readonly maxFileSizeMBOverride!: number
  @Prop({ type: Number, default: 0 }) readonly maxFiles!: number

  @Getter('modelUploadMaxSizeMB', { namespace: Namespace.App })
  public modelUploadMaxSizeMB!: number

  public $refs!: Vue['$refs'] & {
    fileInput: HTMLInputElement | undefined
    listItems: HTMLElement[] | undefined
  }

  private isDragging: boolean = false
  private dragDepth: number = 0
  private files: File[] = []
  private allFiles: File[] = []
  private filesList: FileWithMetadata[] = []
  private calculatedMaxHeight: string = 'auto'

  private getFileKey(file: Pick<File, 'name' | 'size' | 'type' | 'lastModified'>): string {
    return `${file.name}__${file.size}__${file.type}__${file.lastModified}`
  }

  get maxFileSize(): number {
    const sizeMB = this.maxFileSizeMBOverride > 0 ? this.maxFileSizeMBOverride : this.modelUploadMaxSizeMB
    return sizeMB * TeamLibraryConst.MaxFileSizeUnit.mb
  }

  get fileInput(): HTMLInputElement {
    return this.$refs.fileInput as HTMLInputElement
  }

  get selectedFilesSummary(): string {
    const fileSizeSum: number = this.files.reduce((partialSum: number, file: File) => partialSum + file.size, 0)

    const fileSizeString: string = this.formatFileSize(fileSizeSum)

    return `${this.files.length} file${this.files.length === 1 ? '' : 's'} (${fileSizeString} in total)`
  }

  get allowedModelFormatsInfo(): string {
    return this.accept.join(' / ').toUpperCase()
  }

  get instructionCopy(): string {
    if (this.customInstruction) return this.customInstruction

    let insCopy: string = this.allowedModelFormatsInfo

    if (this.fileSizeCheck) insCopy = `${this.maxFileSizeString} max. per file<br />${insCopy}`

    return insCopy
  }

  get maxFileSizeString(): string {
    return this.formatFileSize(this.maxFileSize)
  }

  private async updateListMaxHeight(): Promise<void> {
    await this.$nextTick()

    if (this.$refs.listItems && Array.isArray(this.$refs.listItems) && this.$refs.listItems.length > 0) {
      const firstItem = this.$refs.listItems[0] as HTMLElement
      const itemHeight = firstItem.offsetHeight
      this.calculatedMaxHeight = `${itemHeight * 3.5 + 8}px` // 3 + half <li> tags + 8px margin gap
    }
  }

  triggerFileInput(): void {
    if (this.fileInput == null) return

    this.fileInput.click()
  }

  onDragEnter(event: DragEvent): void {
    this.dragDepth += 1
    this.isDragging = true
  }

  onDragLeave(event: DragEvent): void {
    this.dragDepth -= 1
    // Only when we've fully left the container AND all its children
    if (this.dragDepth <= 0) {
      this.dragDepth = 0
      this.isDragging = false
    }
  }

  onDrop(event: DragEvent): void {
    // Handle your drop here
    this.dragDepth = 0
    this.isDragging = false

    if (!event.dataTransfer || !event.dataTransfer.files) return

    const droppedFiles = event.dataTransfer.files
    this.handleFiles(Array.from(droppedFiles))
  }

  onFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement
    if (target.files) {
      this.handleFiles(Array.from(target.files))
    }
  }

  async handleFiles(newFiles: File[]): Promise<void> {
    // Filter out files that are already selected
    const existingFileKeys = new Set(this.filesList.map((file) => this.getFileKey(file)))
    const filteredFiles = newFiles.filter((file) => !existingFileKeys.has(this.getFileKey(file)))

    const validatedFiles: FileWithMetadata[] = await this.validateFiles(filteredFiles)

    if (this.multiple) {
      this.filesList.push(...validatedFiles)
    } else {
      this.filesList = validatedFiles.slice(0, 1)
    }

    if (this.maxFiles > 0) {
      this.enforceMaxFiles()
    }

    this.updateListMaxHeight()
    this.$emit('files-selected', this.files)
  }

  private enforceMaxFiles(): void {
    // Reset any previous file count errors back to valid before re-evaluating.
    for (let i = 0; i < this.filesList.length; i++) {
      if (this.filesList[i].validationStatus === TeamLibraryConst.FileStatus.FileCountError) {
        this.filesList[i].isValid = true
        this.filesList[i].validationStatus = TeamLibraryConst.FileStatus.FileValid
      }
    }

    // Rebuild the valid files list from filesList (preserving original order).
    this.files = []
    for (let i = 0; i < this.filesList.length; i++) {
      const item = this.filesList[i]
      if (!item.isValid) {
        continue
      }
      if (this.files.length < this.maxFiles) {
        // Re-add from allFiles to keep the original File reference.
        const originalFile = this.allFiles.find((f) => this.getFileKey(f) === this.getFileKey(item))
        if (originalFile) {
          this.files.push(originalFile)
        }
      } else {
        item.isValid = false
        item.validationStatus = TeamLibraryConst.FileStatus.FileCountError
      }
    }
  }

  async validateFiles(files: File[]): Promise<FileWithMetadata[]> {
    const validatedFiles = await Promise.all(
      files.map(async (file: File) => {
        const fileExtensionString = file.name.split('.').pop()
        const fileExtension = fileExtensionString ? `.${fileExtensionString.toLowerCase()}` : ''
        const isAccepted = this.accept.some(
          (type) => type === fileExtension || (type.includes('*') && file.type.startsWith(type.replace('*', '')))
        )

        let validationStatus: TeamLibraryConst.FileStatus = TeamLibraryConst.FileStatus.FileValid
        let isValid: boolean = true
        let customErrorMsg: string = ''

        if (!isAccepted) {
          validationStatus = TeamLibraryConst.FileStatus.FileTypeError
          isValid = false
        } else if (this.fileSizeCheck && this.maxFileSize > 0 && file.size > this.maxFileSize) {
          validationStatus = TeamLibraryConst.FileStatus.FileSizeError
          isValid = false
        } else if (this.fileDimensionCheck && file.type.startsWith('image/')) {
          const result = await ValidationRules.ImageSizeLibraryThumbnail(file)

          if (!result.valid) {
            validationStatus = TeamLibraryConst.FileStatus.FileDimensionError
            isValid = false
            customErrorMsg = `${this.fileStatusMessage(validationStatus)}. Supplied dimension ${result.x} x ${result.y}.`
          }
        }

        if (isValid && this.multiple) {
          this.files.push(file)
          this.allFiles.push(file)
        } else if (isValid) {
          this.files = [file]
          this.allFiles = [file]
        }

        return {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isValid,
          validationStatus,
          customErrorMsg,
        } as FileWithMetadata
      })
    )

    return validatedFiles
  }

  isFileCountError(file: FileWithMetadata): boolean {
    return file.validationStatus === TeamLibraryConst.FileStatus.FileCountError
  }

  removeFile(fileToRemove: Pick<File, 'name' | 'size' | 'type' | 'lastModified'>): void {
    const targetKey = this.getFileKey(fileToRemove)
    this.files = this.files.filter((file: File) => this.getFileKey(file) !== targetKey)
    this.allFiles = this.allFiles.filter((file: File) => this.getFileKey(file) !== targetKey)

    if (this.allFiles.length === 0) {
      this.filesList = []
    } else {
      this.filesList = this.filesList.filter((fileItem: FileWithMetadata) => this.getFileKey(fileItem) !== targetKey)

      if (this.maxFiles > 0) {
        this.enforceMaxFiles()
      }

      this.updateListMaxHeight()
    }
    this.$emit('on-files-deleted', this.filesList)
    this.$emit('files-selected', this.files)
  }

  fileStatusMessage(validationStatus: TeamLibraryConst.FileStatus, customErrorMsg?: string): string {
    if (customErrorMsg) return customErrorMsg

    if (validationStatus === TeamLibraryConst.FileStatus.FileSizeError) {
      return `Exceeds ${this.maxFileSizeString}`
    }

    return TeamLibraryConst.FileStatusMessage[validationStatus]
  }

  formatFileSize(bytes: number): string {
    return helpers.formatFileSize(bytes)
  }

  clearFiles(): void {
    this.files = []
    this.allFiles = []
    this.filesList = []

    if (this.fileInput) {
      this.fileInput.value = ''
    }

    this.$emit('files-selected', this.files)
  }
}
